import { Link } from "react-router-dom";
import { Crown } from "lucide-react";
import { useState } from "react";
import { fmtDate } from "@/lib/plan";
import { TopUpDialog } from "@/components/TopUpDialog";

const Meter = ({ label, used, cap, testId }) => {
  if (cap == null) return null;
  const pct = cap ? Math.min(100, (used / cap) * 100) : 100;
  return (
    <div data-testid={testId}>
      <div className="flex justify-between text-xs"><span className="text-ink-3">{label}</span><span className="font-mono font-semibold text-ink">{used}/{cap}</span></div>
      <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-elevated"><div className={`h-full rounded-full ${pct >= 100 ? "bg-amber-500" : "bg-aqua-solid"}`} style={{ width: `${pct}%` }} /></div>
    </div>
  );
};

export function PlanUsage({ plan, onChange }) {
  const [topup, setTopup] = useState(false);
  if (!plan?.limits) return null;
  const { limits: l, usage: u, period } = plan;
  const lbl = period.label === "today" ? "today" : "this month";
  return (
    <div data-testid="plan-usage" className="mb-6 flex flex-col gap-5 rounded-2xl border border-line bg-surface p-5 md:flex-row md:items-center">
      <div className="flex items-center gap-3 md:w-64">
        <span className="grid h-10 w-10 place-items-center rounded-xl bg-band text-[var(--brand-cyan)]"><Crown className="h-5 w-5" /></span>
        <div>
          <p className="text-xs uppercase tracking-wider text-ink-3">Your plan</p>
          <p data-testid="plan-usage-name" className="font-bold text-ink">{plan.plan_name}
            {plan.source === "trial" && <span data-testid="plan-usage-trial" className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800">Free trial</span>}
            {plan.source === "grandfathered" && <span className="ml-2 rounded-full bg-aqua-soft px-2 py-0.5 text-[10px] font-semibold text-aqua">Early member</span>}</p>
          {plan.expires_at && <p className="text-[11px] text-ink-3">Until {fmtDate(plan.expires_at)}</p>}
          <p data-testid="plan-usage-reset" className="text-[11px] text-ink-3">Quota resets {period.label === "today" ? "tomorrow" : `on ${fmtDate(period.end)}`}</p>
        </div>
      </div>
      <div className="grid flex-1 grid-cols-1 gap-4 sm:grid-cols-4">
        <Meter label={`Quizzes ${lbl}`} used={u.quizzes} cap={l.quizzes} testId="usage-quizzes" />
        <Meter label={`AI requests ${lbl}`} used={u.ai} cap={l.ai_requests} testId="usage-ai" />
        <Meter label={`AI explanations ${lbl}`} used={u.explanations} cap={l.explanations} testId="usage-explanations" />
        {plan.can_topup && (
          <div data-testid="usage-credits" className="text-xs">
            <p className="text-ink-3">Extra AI requests</p>
            <p className="mt-0.5 font-mono font-semibold text-ink">{plan.credits.remaining}{plan.credits.next_expiry && <span className="ml-1 font-sans font-normal text-ink-3">· use by {fmtDate(plan.credits.next_expiry)}</span>}</p>
            <button type="button" data-testid="buy-topup-btn" onClick={() => setTopup(true)} className="mt-1 font-semibold text-aqua hover:underline">Buy extra AI requests</button>
          </div>
        )}
      </div>
      <TopUpDialog open={topup} packs={plan.topups} onClose={() => setTopup(false)} onDone={() => { setTopup(false); onChange?.(); }} />
      {(plan.plan_id !== "pro" || plan.expires_at) && <Link to="/pricing" data-testid="plan-usage-upgrade" className="self-start rounded-full bg-aqua-solid px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-aqua-solid-hover md:self-center">{plan.plan_id === "pro" ? "Renew" : "Upgrade"}</Link>}
    </div>
  );
}
